import type { CSSProperties } from "react";

export type ExposureBreakdown = "category" | "carrier";

export const EXPOSURE_SEGMENT_COLOR_CLASS = {
	primary700: "bg-chart-primary-700",
	primary500: "bg-chart-primary-500",
	primaryHatch: "bg-chart-primary-hatch",
	sky500: "bg-chart-sky-500",
	emerald700: "bg-chart-emerald-700",
	emeraldHatch: "bg-chart-emerald-hatch",
	hatch: "bg-chart-hatch",
	success700: "bg-chart-success-700",
	success500: "bg-chart-success-500",
	success200: "bg-chart-success-200",
	warning500: "bg-chart-warning-500",
	warning400: "bg-chart-warning-400",
	warning300: "bg-chart-warning-300",
	orange600: "bg-chart-orange-600",
	amber600: "bg-chart-amber-600",
	danger700: "bg-chart-danger-700",
	danger500: "bg-chart-danger-500",
	carrierAccelerant: "bg-viz-carrier-accelerant",
	carrierLexington: "bg-viz-carrier-lexington",
	carrierCoaction: "bg-viz-carrier-coaction",
	headroom: "bg-surface-dark",
} as const;

export type ExposureSegmentColor = keyof typeof EXPOSURE_SEGMENT_COLOR_CLASS;

/** Within a hue, open money is solid and approved money is hatched. */
export type ExposureSegmentEmphasis = "solid" | "hatched";

/**
 * Painted as an overlay rather than folded into the color enum, so the treatment composes
 * with any hue instead of needing an entry per hue and treatment.
 */
export const EXPOSURE_EMPHASIS_OVERLAY: Record<ExposureSegmentEmphasis, CSSProperties | undefined> =
	{
		solid: undefined,
		hatched: {
			backgroundImage:
				"repeating-linear-gradient(45deg, rgb(255 255 255 / 0.65) 0 3px, transparent 3px 7px)",
		},
	};

export type ExposureSegmentPart = {
	label: string;
	amount: number;
};

export type ExposureSegment = {
	id: string;
	label: string;
	/**
	 * Drops the qualifier the column header already carries -- "Open" under BID rather than
	 * "Open Bid". Tooltips and screen readers keep the full `label`.
	 */
	legendLabel?: string;
	amount: number;
	color: ExposureSegmentColor;
	emphasis?: ExposureSegmentEmphasis;
	/** Names the parts a combined segment rolls up, revealed on hover instead of drawn as slices. */
	breakdown?: ExposureSegmentPart[];
};

/**
 * One legend column, holding the slices that share a hue so a group's open, approved, and
 * requested money reads top to bottom instead of wrapping across rows.
 */
export type ExposureSegmentColumn = {
	key: string;
	label: string;
	/** Set on columns whose slices roll up to a subtotal worth printing under a rule. */
	totalLabel?: string;
	/** Amounts only, with no color swatches. Used by the cross-column Total. */
	plain?: boolean;
	segments: ExposureSegment[];
};

export type ExposureBreakdownView = {
	id: ExposureBreakdown;
	limit?: number;
	overLimit?: number;
	columns: ExposureSegmentColumn[];
	/** Fills the rest of the track when the limit is unspent. Listed in the legend only when `showHeadroomInLegend` is set. */
	headroom?: ExposureSegment;
	/** Render headroom as a Capacity column. The bar still draws it as the track fill. */
	showHeadroomInLegend?: boolean;
	/** Keep $0 slices in the legend. The bar still skips them. */
	showZeroLegendRows?: boolean;
	/** Append a Total column that sums each legend row across the other columns. */
	showTotalsColumn?: boolean;
};

/** Account-level limit usage, shown in the card header rather than owned by one breakdown. */
export type ExposureLimitSummary = {
	label: string;
	value: string;
	overLimit: boolean;
};

export const EXPOSURE_BREAKDOWN_LABEL: Record<ExposureBreakdown, string> = {
	category: "Category",
	carrier: "Carrier",
};
